import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '@rabbit/app-context';

/**
 * Custom hook to determine product search behavior based on configuration
 * Centralizes the logic for when to auto-load products vs show suggestions
 */
export function useProductSearchConfig() {
  const { config } = useContext(AppContext);
  const { t } = useTranslation();

  const showProductsFirst =
    config?.HOLDINGS?.PRODUCT?.SHOW_PRODUCTS_FIRST === true;

  let productSuggestions = t('CFG_PRODUCT_SUGGESTIONS', {
    returnObjects: true,
    defaultValue: [],
  }) as unknown as any;
  if (typeof productSuggestions === 'string') {
    productSuggestions = productSuggestions.split(',');
  }

  // Guard against non-array translations
  const hasSuggestions =
    Array.isArray(productSuggestions) && productSuggestions.length > 0;

  const shouldAutoLoadProducts =
    showProductsFirst || (!showProductsFirst && !hasSuggestions);

  const shouldShowSuggestions = useCallback(
    (hasSearchValue: boolean = false) =>
      !hasSearchValue && !showProductsFirst && hasSuggestions,
    [showProductsFirst, hasSuggestions]
  );

  const shouldShowProducts = useCallback(
    (hasSearchValue: boolean = false) =>
      hasSearchValue || shouldAutoLoadProducts,
    [shouldAutoLoadProducts]
  );

  return {
    shouldAutoLoadProducts,
    shouldShowSuggestions,
    shouldShowProducts,
    config,
    t,
  };
}
